import { Link } from "react-router-dom";
import { useState } from "react";
import { LazyImage } from "@/components/LazyImage";
import { ArrowUpRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { useHomeWallSettings, buildWallGridClasses, wallDefaults, type WallKey } from "@/hooks/useHomeWallSettings";

interface WallBrand {
  name: string;
  category: string;
  slug: string;
  logo: string;
  website: string;
  heroImage?: string | null;
  hidden?: boolean;
}

interface BrandWallProps {
  id?: string;
  eyebrow?: string;
  title: string;
  description: string;
  brands: WallBrand[];
  wallKey?: WallKey;
}

/**
 * Wall of Brands — griglia statica responsive.
 * Hover: appare l'hero image del marchio + overlay scuro, logo si schiarisce
 * (brightness-0 invert), nome/categoria/freccia in bianco.
 */
const BrandWallItem = ({ brand }: { brand: WallBrand }) => {
  const [error, setError] = useState(false);
  const isInternal = brand.website.startsWith("/");
  const hero = brand.heroImage || "";

  const inner = (
    <div className="group relative aspect-square w-full overflow-hidden border border-border bg-card transition-all duration-500 hover:border-terracotta hover:scale-[1.01] hover:z-10">
      {/* Hero background (visibile solo su hover) */}
      {hero && (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ backgroundImage: `url(${hero})` }}
          aria-hidden="true"
        />
      )}
      {/* Overlay scuro per leggibilità */}
      {hero && (
        <div className="absolute inset-0 bg-black/55 opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
      )}

      {/* Logo — area centrale ampia per massimo protagonismo */}
      <div className="absolute inset-4 sm:inset-5 flex items-center justify-center transition-all duration-500 group-hover:scale-105">
        {brand.logo && !error ? (
          <LazyImage
            src={brand.logo}
            alt={`${brand.name} logo`}
            className={`w-full h-full transition-[filter] duration-500 ${hero ? "group-hover:[filter:brightness(0)_invert(1)]" : ""}`}
            width={240}
            height={240}
            loading="lazy"
            fit="contain"
            onError={() => setError(true)}
          />
        ) : (
          <span className={`font-display text-2xl text-center transition-colors duration-500 ${hero ? "text-foreground/70 group-hover:text-white" : "text-foreground/70"}`}>
            {brand.name}
          </span>
        )}
      </div>

      {/* Reveal TOP — eyebrow categoria */}
      <div className="absolute inset-x-0 top-0 px-3.5 sm:px-4 pt-3 opacity-0 -translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
        <p
          className={`text-[10px] sm:text-[11px] uppercase tracking-[0.22em] ${hero ? "text-terracotta-soft" : "text-terracotta"}`}
          style={hero ? { textShadow: "0 1px 2px rgba(0,0,0,0.6)" } : undefined}
        >
          {brand.category}
        </p>
      </div>

      {/* Reveal BOTTOM — solo freccia in basso a destra */}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-end px-3.5 sm:px-4 pb-3 sm:pb-3.5 opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
        <ArrowUpRight
          className={`h-5 w-5 shrink-0 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${hero ? "text-white" : "text-terracotta"}`}
          style={hero ? { filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))" } : undefined}
        />
      </div>
    </div>
  );

  return isInternal ? (
    <Link to={brand.website} aria-label={brand.name} className="block">
      {inner}
    </Link>
  ) : (
    <a
      href={brand.website}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={brand.name}
      className="block"
    >
      {inner}
    </a>
  );
};

const BrandWall = ({ id, eyebrow, title, description, brands, wallKey = "marchi-propri" }: BrandWallProps) => {
  const { data: settings } = useHomeWallSettings(wallKey);
  const s = settings || wallDefaults[wallKey];

  if (!s.is_visible) return null;

  let visible = brands.filter((b) => !b.hidden);
  if (s.max_items && s.max_items > 0) visible = visible.slice(0, s.max_items);

  return (
    <section
      id={id}
      className="py-5 md:py-7 border-t border-border bg-background scroll-mt-20"
    >
      <div className="container mx-auto px-6">
        <SectionHeader eyebrow={eyebrow} title={title} description={description} />
        <div className={buildWallGridClasses(s)}>
          {visible.map((brand) => (
            <BrandWallItem key={brand.slug} brand={brand} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandWall;
